/** Choose: this clip's lanes only (GET /api/clips/{id}/lanes.json), on the clip clock, driving the clip preview's
 * player (lane clicks seek it, the playhead follows it). Lanes: sections, cuts, lyrics, beat layers (toggles), TikTok
 * heat and Vision (per-sample mean colour strip, CCT line, subject band). Text twins: the vibe, the beats / cuts /
 * vision tables. Downloads: JSON, CSV, MD. The Canvas card (plate scale/position + candidates) toggles an overlay on
 * the preview: the visible plate window and the detection boxes of the nearest vision sample. */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";
import { api, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { fmtMs } from "../../lib/frames";
import { errorText, Loading } from "../common";
import type { FrameController } from "../player/controller";
import { usePlayerSelect } from "../player/usePlayer";
import { BeatLane, CutsLane, LaneRow, LyricsLane, Playhead, useEdgeClick } from "../timeline/Lanes";
import {
  BEAT_LAYER_LABELS, BEAT_LAYERS, pct, projectHeat, projectSections, type BeatLayer, type Lane, type View,
} from "../timeline/project";
import { BeatsTable } from "../watch/BeatsTable";
import { CutsTable } from "../watch/CutsTable";
import {
  boxStyle, candidates, canvasText, cctPoints, cell, clipTimeline, clipView, lanesUrl, nearestSample, projectVision,
  visibleWindow, type CanvasOut, type ClipLanes, type VisionSample,
} from "./clipLanes";

export const VISION_TABLE_MAX = 300;
type ClipLane = "sections" | "cuts" | "lyrics" | BeatLayer | "heat" | "vision";
const CLIP_LANES: readonly ClipLane[] = ["sections", "cuts", "lyrics", ...BEAT_LAYERS, "heat", "vision"];
const LABELS: Record<ClipLane, string> = { sections: "Sections", cuts: "Cuts", lyrics: "Lyrics", ...BEAT_LAYER_LABELS,
  heat: "TikTok heat", vision: "Vision" };
const DEFAULT_ON: readonly ClipLane[] = CLIP_LANES.filter((l) => l !== "eighths" && l !== "sixteenths");
/** Lanes that only exist on a music video (a song clip has none: no picture, so no cuts or vision). */
const MV_ONLY: ReadonlySet<ClipLane> = new Set<ClipLane>(["cuts", "vision"]);

const at = (left: number, width?: number): CSSProperties =>
  width == null ? { left: `${left}%` } : { left: `${left}%`, width: `${width}%` };

export function useClipLanes(clipId: number | null) {
  return useQuery({
    queryKey: qk.entity("clip", clipId ?? 0, "lanes"),
    enabled: clipId != null,
    queryFn: () => unwrap(api.GET("/api/clips/{clip_id}/lanes.json", { params: { path: { clip_id: clipId as number } } })),
  });
}

export function LanesDownloads({ clipId }: { clipId: number }) {
  return (
    <span className="ws8-row" role="group" aria-label="Download this clip's lanes">
      {(["json", "csv", "md"] as const).map((ext) => (
        <a key={ext} className="button" href={lanesUrl(clipId, ext, true)} download={`clip-${clipId}-lanes.${ext}`}
          data-testid={`lanes-download-${ext}`} aria-label={`Download clip #${clipId} lanes as ${ext.toUpperCase()}`}>
          {ext.toUpperCase()}
        </a>
      ))}
    </span>
  );
}

function VisionLane({ samples, view, ctrl }: { samples: readonly VisionSample[]; view: View; ctrl: FrameController }) {
  const cells = useMemo(() => projectVision(samples, view), [samples, view]);
  const line = useMemo(() => cctPoints(cells), [cells]);
  return (
    <LaneRow lane="vision" label="Vision" view={view} ctrl={ctrl} className="ws8-lane-vision">
      {cells.map((c) => (
        <span key={c.key} aria-hidden="true" className={`ws8-vis-cell${c.mono ? " ws8-vis-mono" : ""}`} data-t={c.t}
          style={{ ...at(c.left, c.width), background: c.colour }} />
      ))}
      {cells.filter((c) => c.subject > 0).map((c) => (
        <span key={`s${c.key}`} aria-hidden="true" className="ws8-vis-subject"
          style={{ ...at(c.left, c.width), height: `${Math.round(c.subject * 40)}%` }} />
      ))}
      {line && (
        <svg className="ws8-vis-cct" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <polyline points={line} vectorEffect="non-scaling-stroke" data-testid="vision-cct-line" />
        </svg>
      )}
    </LaneRow>
  );
}

function VibeSummary({ l }: { l: ClipLanes }) {
  const v = l.vibe;
  if (!v) return <p className="ws8-muted" data-testid="vibe-summary">No vision samples in this clip yet (the video's vision job has not run).</p>;
  return (
    <div data-testid="vibe-summary">
      <p><strong>Vibe:</strong> {v.text}</p>
      {v.grid.length > 0 && (
        <table className="ws8-table ws8-vibe-grid" data-testid="vibe-grid">
          <caption>Average colour per 4×4 cell of the frame (hex, luma 0–1)</caption>
          <tbody>
            {v.grid.map((row, r) => (
              <tr key={r}>
                {row.map((c) => (
                  <td key={c.col}>
                    <span className="ws8-swatch" aria-hidden="true" style={{ background: c.hex ?? "transparent" }} />
                    {c.hex ?? "–"} {c.luma != null ? c.luma.toFixed(2) : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {v.dominant.length > 0 && (
        <p className="ws8-muted">Top colours:{" "}
          {v.dominant.map((d) => (
            <span key={d.hex} className="ws8-inline">
              <span className="ws8-swatch" aria-hidden="true" style={{ background: d.hex }} />{d.hex} ({(d.weight * 100).toFixed(0)}%){" "}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function VisionTable({ l, ctrl, agent }: { l: ClipLanes; ctrl: FrameController; agent: boolean }) {
  const shown = l.vision.slice(0, VISION_TABLE_MAX);
  return (
    <details className="ws8-panel" open={agent} data-testid="vision-panel">
      <summary><h3 className="ws8-h2 ws8-inline-h">Vision table</h3></summary>
      <p className="ws8-muted">
        {l.vision_run ? `${l.vision_run.samples} samples over the video (${l.vision_run.fps ?? "?"} fps${
          l.vision_run.model ? `, ${l.vision_run.model}` : ""}); ` : ""}
        {l.vision.length > shown.length ? `first ${shown.length} of ${l.vision.length}` : l.vision.length} in this clip.
      </p>
      {l.vision.length === 0 ? <p data-testid="vision-empty">No vision samples in this clip.</p> : (
        <div className="ws8-scroll" tabIndex={0} role="region" aria-label="Vision samples in this clip">
          <table className="ws8-table ws8-beats-table" data-testid="vision-table">
            <caption>Vision samples (clip clock)</caption>
            <thead>
              <tr><th scope="col">Time</th><th scope="col">MV</th><th scope="col">Colour</th><th scope="col">CCT</th>
                <th scope="col">Luma (p5–p95)</th><th scope="col">Mono</th><th scope="col">Top colours</th>
                <th scope="col">Persons / heads</th><th scope="col">Seek</th></tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.t_clip_ms} data-testid={`vision-row-${s.t_clip_ms}`}>
                  <td>{fmtMs(s.t_clip_ms)}</td>
                  <td>{fmtMs(s.t_mv_ms)}</td>
                  <td><span className="ws8-swatch" aria-hidden="true" style={{ background: s.mean_hex ?? "transparent" }} />{s.mean_hex ?? "–"}</td>
                  <td>{s.cct_k != null ? `${Math.round(s.cct_k)} K` : "–"}{s.cct_class ? ` ${s.cct_class}` : ""}</td>
                  <td>{s.luma_mean != null ? s.luma_mean.toFixed(2) : "–"}
                    {s.luma_p5 != null && s.luma_p95 != null ? ` (${s.luma_p5.toFixed(2)}–${s.luma_p95.toFixed(2)})` : ""}</td>
                  <td>{s.mono == null ? "–" : s.mono ? "yes" : "no"}</td>
                  <td>{s.dominant.slice(0, 3).map((d) => d.hex).join(" ") || "–"}</td>
                  <td>{s.saliency ? "saliency only" : `${s.persons} / ${s.heads}`}{s.persons ? ` · ${(s.subject_area * 100).toFixed(0)}%` : ""}</td>
                  <td>
                    <button type="button" onClick={() => ctrl.seekMs(s.t_clip_ms)}
                      aria-label={`Seek to the vision sample at ${fmtMs(s.t_clip_ms)}`}>Seek</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}

export function CanvasCard({ canvas, overlay, setOverlay }: {
  canvas: CanvasOut | null | undefined; overlay: boolean; setOverlay: (on: boolean) => void;
}) {
  const cand = canvas ? candidates(canvas) : { cols: [], rows: [] };
  return (
    <section className="ws8-panel" aria-labelledby="ws8-canvas-h" data-testid="canvas-card">
      <h3 id="ws8-canvas-h" className="ws8-h2">Canvas</h3>
      {!canvas ? <p className="ws8-muted">No canvas solution for this clip.</p> : (
        <>
          <p data-testid="canvas-summary">{canvasText(canvas)}</p>
          <p className="ws8-muted">{canvas.source}{canvas.version ? ` (${canvas.version})` : ""}: {canvas.reason || "no reason given"}</p>
          {cand.rows.length > 0 && (
            <div className="ws8-scroll" tabIndex={0} role="region" aria-label="Canvas candidates">
              <table className="ws8-table" data-testid="canvas-candidates">
                <caption>Candidates the solver weighed</caption>
                <thead><tr>{cand.cols.map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
                <tbody>
                  {cand.rows.map((r, i) => <tr key={i}>{cand.cols.map((c) => <td key={c}>{cell(r[c])}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <label className="ws8-inline">
        <input type="checkbox" checked={overlay} onChange={(e) => setOverlay(e.target.checked)} data-testid="canvas-overlay-toggle" />
        {" "}Show the visible plate window and detections on the preview
      </label>
    </section>
  );
}

/** Drawn over the clip preview (in percent of the 16:9 frame): blur-bar shades outside the visible plate window,
 * the window's frame, and the boxes (person / head / saliency) of the vision sample nearest the playhead. */
export function CanvasOverlay({ l, ctrl }: { l: ClipLanes; ctrl: FrameController }) {
  const t = usePlayerSelect(ctrl, (s) => nearestSample(l.vision, s.tMs)?.t_clip_ms ?? null);
  const s = t == null ? null : l.vision.find((x) => x.t_clip_ms === t) ?? null;
  const win = l.canvas ? visibleWindow(l.canvas) : null;
  return (
    <>
      {win && (
        <>
          <div className="ws8-ov-shade" style={{ left: 0, width: `${win.x0 * 100}%` }} />
          <div className="ws8-ov-shade" style={{ left: `${win.x1 * 100}%`, right: 0 }} />
          <div className="ws8-ov-window" data-testid="overlay-window"
            style={{ left: `${win.x0 * 100}%`, width: `${(win.x1 - win.x0) * 100}%` }} />
        </>
      )}
      {s?.objects.map((o, i) => {
        const b = boxStyle(o.box);
        const h = o.head_box ? boxStyle(o.head_box) : null;
        return (
          <span key={i}>
            {b && <span className={`ws8-ov-box${o.name === "saliency" ? " ws8-ov-sal" : ""}`} style={b} data-testid="overlay-box">
              <span className="ws8-ov-tag">{o.name}{o.conf != null ? ` ${o.conf.toFixed(2)}` : ""}</span></span>}
            {h && <span className="ws8-ov-box ws8-ov-head" style={h} />}
          </span>
        );
      })}
    </>
  );
}

export function ClipLanesPanel({ clipId, q, ctrl, agent, overlay, setOverlay }: {
  clipId: number; q: ReturnType<typeof useClipLanes>; ctrl: FrameController | null; agent: boolean; overlay: boolean;
  setOverlay: (on: boolean) => void;
}) {
  const [on, setOn] = useState<Set<ClipLane>>(() => new Set(DEFAULT_ON));
  const [beatFilter, setBeatFilter] = useState<string | null>(null);
  const l = q.data;
  const tl = useMemo(() => (l ? clipTimeline(l) : null), [l]);
  const view = useMemo(() => clipView(l ?? { dur_ms: 1 }), [l]);
  const toggle = (x: ClipLane) => {
    const next = new Set(on);
    if (next.has(x)) next.delete(x);
    else next.add(x);
    setOn(next);
  };
  const beatLanes = useMemo(() => new Set<Lane>(BEAT_LAYERS.filter((b) => on.has(b))), [on]);
  return (
    <section className="ws8-panel ws8-clip-lanes" aria-labelledby="ws8-clip-lanes-h" data-testid="clip-lanes">
      <div className="ws8-row">
        <h2 id="ws8-clip-lanes-h" className="ws8-h2">This clip's lanes</h2>
        <LanesDownloads clipId={clipId} />
      </div>
      {q.isPending && <Loading what="the clip's lanes" />}
      {q.isError && <p className="ws8-badge ws8-badge-bad" role="alert" data-testid="clip-lanes-error">Lanes unavailable: {errorText(q.error)}</p>}
      {l && tl && (
        <>
          <p className="ws8-muted" data-testid="clip-lanes-window">
            Clip clock 0:00.000–{fmtMs(l.dur_ms)} {l.kind === "song"
              ? `(song clip, no music video: song ${fmtMs(l.t_song_in_ms)}–${fmtMs(l.t_song_out_ms)} + fade; song-clock lanes only)`
              : `(MV ${fmtMs(l.t_mv_in_ms)}–${fmtMs(l.t_mv_out_ms)})`}
            {l.bpm != null ? ` · ${l.bpm.toFixed(2)} BPM ${l.meter ?? ""}` : ""} · {l.sections.length} section
            {l.sections.length === 1 ? "" : "s"}, {l.lines.length} line{l.lines.length === 1 ? "" : "s"}, {l.scenes.length} transition
            {l.scenes.length === 1 ? "" : "s"}, {l.vision.length} vision sample{l.vision.length === 1 ? "" : "s"}.
          </p>
          {l.kind !== "song" && <VibeSummary l={l} />}
          <div className="ws8-row" role="group" aria-label="Clip lane options">
            {CLIP_LANES.filter((x) => l.kind !== "song" || !MV_ONLY.has(x)).map((x) => (
              <label key={x} className="ws8-inline">
                <input type="checkbox" checked={on.has(x)} onChange={() => toggle(x)} data-testid={`clip-lane-toggle-${x}`} />
                {LABELS[x]}
              </label>
            ))}
          </div>
          {ctrl ? <LaneStack l={l} tl={tl} view={view} ctrl={ctrl}
            on={l.kind === "song" ? new Set([...on].filter((x) => !MV_ONLY.has(x))) : on} /> : <Loading what="the clip player" />}
          {l.kind !== "song" && <CanvasCard canvas={l.canvas} overlay={overlay} setOverlay={setOverlay} />}
          {ctrl && (
            <>
              <BeatsTable tl={tl} ctrl={ctrl} view={view} lanes={beatLanes} filter={beatFilter} setFilter={setBeatFilter}
                agent={agent} clock="clip clock" />
              {l.kind !== "song" && <CutsTable tl={tl} ctrl={ctrl} view={view} agent={agent} clock="clip clock" />}
              {l.kind !== "song" && <VisionTable l={l} ctrl={ctrl} agent={agent} />}
            </>
          )}
        </>
      )}
    </section>
  );
}

function LaneStack({ l, tl, view, ctrl, on }: {
  l: ClipLanes; tl: ReturnType<typeof clipTimeline>; view: View; ctrl: FrameController; on: Set<ClipLane>;
}) {
  const edge = useEdgeClick(ctrl);
  const sections = useMemo(() => projectSections(tl, view), [tl, view]);
  const heat = useMemo(() => projectHeat(tl, view), [tl, view]);
  return (
    <div className="ws8-lane-stack" data-testid="clip-lane-stack">
      {on.has("sections") && (
        <LaneRow lane="sections" view={view} ctrl={ctrl}>
          {sections.map((s) => (
            <button key={s.key} type="button" className={`ws8-item ws8-section${s.chorus ? " ws8-chorus" : ""}`}
              style={at(s.left, s.width)} data-testid={`clip-lane-section-${s.idx}`}
              aria-label={`Section ${s.label} ${fmtMs(s.t0)}–${fmtMs(s.t1)} (clip clock)${s.performer ? `, ${s.performer}` : ""}`}
              onClick={(e) => edge(e, { inMs: s.t0, outMs: s.t1 }, `section ${s.label}`)}>{s.label}</button>
          ))}
        </LaneRow>
      )}
      {on.has("cuts") && <CutsLane tl={tl} view={view} ctrl={ctrl} edge={edge} />}
      {on.has("lyrics") && (
        <LaneRow lane="lyrics" view={view} ctrl={ctrl}>
          <LyricsLane tl={tl} view={view} mode="masked" ctrl={ctrl} edge={edge} />
        </LaneRow>
      )}
      {BEAT_LAYERS.filter((b) => on.has(b)).map((b) => <BeatLane key={b} tl={tl} layer={b} view={view} ctrl={ctrl} />)}
      {on.has("heat") && (
        <LaneRow lane="heat" view={view} ctrl={ctrl}>
          {heat.map((h) => (
            <span key={h.key} aria-hidden="true" className="ws8-heat" style={{ ...at(h.left, h.width), opacity: 0.15 + 0.85 * h.intensity }} />
          ))}
          {tl.hot_windows.map((w, i) => (
            <button key={i} type="button" className="ws8-item ws8-hot" style={at(pct(w.t0_mv_ms, view), pct(w.t1_mv_ms, view) - pct(w.t0_mv_ms, view))}
              aria-label={`Hot TikTok window ${fmtMs(w.t0_mv_ms)}–${fmtMs(w.t1_mv_ms)} (clip clock) score ${w.score.toFixed(2)}`}
              onClick={(e) => edge(e, { inMs: w.t0_mv_ms, outMs: w.t1_mv_ms }, "hot window")}>hot {w.score.toFixed(2)}</button>
          ))}
        </LaneRow>
      )}
      {on.has("vision") && <VisionLane samples={l.vision} view={view} ctrl={ctrl} />}
      <Playhead ctrl={ctrl} view={view} />
    </div>
  );
}
